import React, { useState, useRef, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import { Security, PlayArrow, Pause, VolumeUp, LocationOn } from '@material-ui/icons';
import { format } from 'date-fns';
import { SENDER_TYPE } from 'utils/constants';

const styles = () => ({
  text: {
    padding: '12px 16px',
    borderRadius: '16px',
    display: 'inline-block',
    fontSize: '14px',
    fontFamily: 'montserratmedium',
    maxWidth: '75%',
    wordWrap: 'break-word',
    position: 'relative',
    transition: 'all 0.2s ease',
  },
  receiverMessage: {
    textAlign: 'left',
    marginBottom: '8px',
    animation: '$slideInLeft 0.3s ease',
    '& $text': {
      backgroundColor: '#eaf5fc',
      color: '#000000',
      borderTopLeftRadius: '4px',
    }
  },
  senderMessage: {
    textAlign: 'right',
    marginBottom: '8px',
    animation: '$slideInRight 0.3s ease',
    '& $text': {
      backgroundColor: '#E1D7F0',
      color: '#000000',
      borderTopRightRadius: '4px',
    }
  },
  picapMessage: {
    textAlign: 'right',
    marginBottom: '8px',
    animation: '$slideInRight 0.3s ease',
    '& $text': {
      backgroundColor: '#E1D7F0',
      color: '#000000',
      borderTopRightRadius: '4px',
    }
  },
  time: {
    fontSize: '11px',
    color: '#8E8E93',
    marginTop: '4px',
    marginBottom: '0',
    fontFamily: 'montserratmedium',
    fontWeight: 400,
  },
  messageContainer: {
    padding: '4px 0',
  },
  adminBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: '#FF9500',
    fontSize: '13px',
    marginBottom: '4px',
    fontWeight: 500,
    '& svg': {
      fontSize: '16px',
    }
  },
  '@keyframes slideInLeft': {
    from: {
      opacity: 0,
      transform: 'translateX(-20px)'
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)'
    }
  },
  '@keyframes slideInRight': {
    from: {
      opacity: 0,
      transform: 'translateX(20px)'
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)'
    }
  },
  // Estilos para archivos adjuntos
  fileAttachment: {
    maxWidth: '100%',
  },
  image: {
    maxWidth: '250px',
    maxHeight: '250px',
    borderRadius: '8px',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.9
    }
  },
  audio: {
    width: '100%',
    height: '40px',
    outline: 'none',
    '&::-webkit-media-controls-panel': {
      backgroundColor: 'transparent',
    }
  },
  video: {
    maxWidth: '300px',
    maxHeight: '250px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  fileName: {
    fontSize: '12px',
    color: '#666',
    marginTop: '5px',
    fontStyle: 'italic'
  },
  locationMap: {
    width: '100%',
    maxWidth: '300px',
    height: '200px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.9
    }
  },
  locationButton: {
    marginTop: '4px',
    padding: '12px 24px',
    backgroundColor: '#6C5DD3',
    color: 'white',
    borderRadius: '25px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'montserratmedium',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(108, 93, 211, 0.3)',
    '& svg': {
      fontSize: '20px',
    },
    '&:hover': {
      backgroundColor: '#5B4BC4',
      boxShadow: '0 4px 12px rgba(108, 93, 211, 0.4)',
      transform: 'translateY(-1px)',
    }
  },
  audioPlayer: {
    marginTop: '0px',
    padding: '0px',
    backgroundColor: 'transparent',
    borderRadius: '0px',
    width: '100%',
  },
  audioWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
  },
  playButton: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    '& svg': {
      color: '#5E5CE6',
      fontSize: '28px',
    }
  },
  audioInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  audioFileName: {
    fontSize: '13px',
    color: '#000000',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  audioProgress: {
    width: '100%',
    height: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  audioProgressBar: {
    height: '100%',
    backgroundColor: '#5E5CE6',
    width: '30%', // Se puede hacer dinámico con state
    borderRadius: '2px',
  }
});

// Componente para el reproductor de audio
const AudioPlayer = ({ fileUrl, fileName, classes }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      const progress = (audio.currentTime / audio.duration) * 100;
      setProgress(progress);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <div className={classes.fileAttachment}>
      <div className={classes.audioWrapper}>
        {/* Botón de Play/Pause */}
        <button className={classes.playButton} onClick={togglePlay}>
          {isPlaying ? <Pause /> : <PlayArrow />}
        </button>

        {/* Información del audio */}
        <div className={classes.audioInfo}>
          <div className={classes.audioFileName}>
            {fileName || 'Audio message'}
          </div>
          <div className={classes.audioProgress}>
            <div
              className={classes.audioProgressBar}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Ícono de volumen */}
        <VolumeUp style={{ color: '#8E8E93', fontSize: '20px' }} />
      </div>

      {/* Audio element oculto */}
      <audio
        ref={audioRef}
        src={fileUrl}
        style={{ display: 'none' }}
      >
        Tu navegador no soporta el elemento de audio.
      </audio>
    </div>
  );
};

const BookingChatMessage = ({
  classes,
  message,
  isGrpc = false
}) => {
  const messageClassName = () => {
    let returnedClass = classes.picapMessage;
    if (message.sender === SENDER_TYPE.PASSENGER) {
      returnedClass = classes.senderMessage;
    } else if (message.sender === SENDER_TYPE.DRIVER) {
      returnedClass = classes.receiverMessage;
    }
    return returnedClass;
  };

  const formattedTime = () => {
    const { createdAt } = message;
    if (createdAt) {
      return format(new Date(createdAt), 'dd/MM/yyyy, hh:mm a')
    }
    return '';
  };

  // Renderizar ubicación con enlace a Google Maps
  const renderLocationAttachment = () => {
    const { coordinates } = message;

    if (!coordinates || !coordinates.lat || !coordinates.lon) return null;

    const { lat, lon } = coordinates;

    // URL para abrir en Google Maps
    const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lon}`;

    return (
      <div className={classes.fileAttachment}>
        <button
          className={classes.locationButton}
          onClick={() => window.open(googleMapsUrl, '_blank')}
        >
          <LocationOn /> Ver en Google Maps
        </button>
      </div>
    );
  };

  // Determinar el tipo de archivo según file_kind
  const renderFileAttachment = () => {
    const { fileUrl, fileName, fileKind } = message;

    // Si es ubicación (fileKind: 3), usar coordinates
    if (fileKind === 3) {
      return renderLocationAttachment();
    }

    // Si no hay archivo adjunto, no renderizar nada
    if (!fileUrl) return null;

    switch (fileKind) {
      case 1: // Imagen
        return (
          <div className={classes.fileAttachment}>
            <img
              src={fileUrl}
              alt={fileName || 'Imagen'}
              className={classes.image}
              onClick={() => window.open(fileUrl, '_blank')}
            />
            {fileName && <div className={classes.fileName}>{fileName}</div>}
          </div>
        );

      case 2: // Video
        return (
          <div className={classes.fileAttachment}>
            <video
              src={fileUrl}
              controls
              className={classes.video}
            >
              Tu navegador no soporta el elemento de video.
            </video>
            {fileName && <div className={classes.fileName}>{fileName}</div>}
          </div>
        );

      case 5: // Audio
        return <AudioPlayer fileUrl={fileUrl} fileName={fileName} classes={classes} />;

      case 3: // Ubicación
        return renderLocationAttachment();

      default:
        // Otros tipos de archivos (documentos, etc.)
        if (fileUrl) {
          return (
            <div className={classes.fileAttachment}>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.fileName}
              >
                <span role="img" aria-label="adjunto">📎</span> {fileName || 'Ver archivo'}
              </a>
            </div>
          );
        }
        return null;
    }
  };

  const isRightAligned = message.sender === SENDER_TYPE.PASSENGER || message.sender === SENDER_TYPE.PICAP;

  return (
    <Grid item xs={12} className={classes.messageContainer}>
      <div className={messageClassName()}>
        {/* Badge de Soporte/Picap - Solo mostrar en gRPC */}
        {isGrpc && message.sender === SENDER_TYPE.PICAP && (
          <div style={{ textAlign: 'right', marginBottom: '4px' }}>
            <span className={classes.adminBadge}>
              <Security style={{ fontSize: '14px' }} /> Soporte
            </span>
          </div>
        )}

        <div className={classes.text}>
          {/* Mostrar texto del mensaje si existe */}
          {message.message && <div>{message.message}</div>}
          {/* Mostrar archivo adjunto si existe */}
          {renderFileAttachment()}
        </div>

        {/* Timestamp fuera de la burbuja */}
        <p className={classes.time} style={{ textAlign: isRightAligned ? 'right' : 'left' }}>
          {formattedTime()}
        </p>
      </div>
    </Grid>
  );
};

export default withStyles(styles)(BookingChatMessage);